<template>
  <TileFrame :size="size" @click="handleTileClick">
    <template #image>
      <ImageBasic
        :image-data="tileData.image"
        class="object-cover w-full h-full aspect-video"
        sizes="768px md:460px"
        quality="80"
      ></ImageBasic>
      <div
        v-if="
          tileData.typeHandle === TileTypeHandle.OFFER &&
          tileData.offer.isDigitalExcellence
        "
        class="absolute top-0 right-sm"
      >
        <img src="@/assets/images/digital-excellence-badge.svg" />
      </div>
    </template>

    <div class="grid h-full p-md">
      <div ref="overline" class="text-sm font-light">
        {{ tileData.overline }}
      </div>
      <h4 class="break-words pt-sm">
        {{ tileData.title }}
      </h4>
      <p class="pt-sm">
        {{ tileData.description }}
      </p>
      <div class="pt-sm min-h-[50px]">
        <div class="flex flex-col gap-y-2xs [&_div]:h-[20px]">
          <div
            v-if="tileData.offer.manufacturer"
            class="flex flex-row items-baseline flex-nowrap"
          >
            <FaIcon
              icon-class="fal fa-hand-holding-box"
              classes="mr-sm fa-fw"
            />
            <span>{{ tileData.offer.manufacturer }}</span>
          </div>
          <div
            v-if="tileData.offer.validUntil"
            class="flex flex-row items-baseline flex-nowrap"
          >
            <FaIcon icon-class="fal fa-calendar-alt" classes="mr-sm fa-fw" />
            <span
              >Angebot gültig bis
              {{ formatDate(tileData.offer.validUntil) }}</span
            >
          </div>
        </div>
      </div>
      <div class="flex flex-col [&_div]:ml-auto [&_div]:leading-xs mt-md">
        <div
          v-if="
            tileData.offer.obsoletePrice !== 0 && tileData.offer.price !== 0
          "
          class="text-lg text-vdv-border-dark mb-xs"
        >
          LVP
          <s
            ><b>{{ n(tileData.offer.obsoletePrice, 'currency') }}</b></s
          >
        </div>
        <div v-else>&nbsp;</div>
        <div v-if="tileData.offer.price">
          <span v-if="tileData.offer.priceFrom">ab&nbsp;</span>
          <span class="text-3xl font-bold">{{
            n(tileData.offer.price, 'currency')
          }}</span>
        </div>
        <div v-else>
          <span class="text-3xl font-bold">{{
            t('offer.tile.notSpecified')
          }}</span>
        </div>
        <div class="text-sm">
          {{
            tileData.offer.price
              ? t('offer.tile.inclVat')
              : t('offer.tile.requestPrice')
          }}
        </div>
      </div>
      <div class="relative flex flex-col self-end pt-md">
        <LinkBasic
          :label="t('basicLink.showDetails')"
          :link="`/${tileData.uri}`"
          :filter-params="queryArgs"
          target="_self"
          icon="fas fa-chevron-right"
          class="ml-auto text-vdv-base"
        />
      </div>
    </div>
  </TileFrame>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import type { LocationQueryRaw } from 'vue-router';
import { ETileSize } from '@/@types/tile-size';
import { formatDate } from '@/helpers/dayJs';
import LinkBasic from '@/components/components/core/basicLink/basic-link.vue';
import TileFrame from '../../tileFrame/TileFrame.vue';
import ImageBasic from '@/components/components/core/basicImage/image-basic.vue';
import type { OfferTileData } from './tileType';
import { TileTypeHandle } from '../../../types';
import FaIcon from '@/components/fa-icon.vue';

const { t, n } = useTrans();

const overline = ref<HTMLDivElement>(null);

defineExpose({ overline });

const props = defineProps({
  tileData: {
    type: Object as PropType<OfferTileData>,
    required: true,
  },
  size: {
    type: String as PropType<ETileSize>,
    required: false,
    default: ETileSize.NORMAL,
  },
  queryArgs: {
    type: Object as PropType<LocationQueryRaw>,
    required: false,
    default: null,
  },
});

function handleTileClick() {
  navigateTo({
    path: '/' + props.tileData.uri,
    query: props.queryArgs,
  });
}
</script>
<style scoped lang="postcss"></style>
